import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SidebarLayout from "../shops/SidebarLayout";
import FreelancerSidebarLayout from "../freelancer/FreelancerSidebarLayout";
import Step1Welcome from "./steps/Step1Welcome";
import Step2Upload from "./steps/Step2Upload";
import Step3FieldMapping from "./steps/Step3FieldMapping";
import Step4ConflictResolution from "./steps/Step4ConflictResolution";
import Step5ReviewGoLive from "./steps/Step5ReviewGoLive";
import Step6Progress from "./steps/Step6Progress";
import Step7Success from "./steps/Step7Success";

/**
 * Migration Wizard - Multi-step onboarding for businesses migrating from other platforms
 *
 * Flow:
 * 1. Welcome & Platform Selection (Square, Squire, Booksy)
 * 2. CSV Upload (services, customers, appointments)
 * 3. Field Mapping (auto-detect + manual override)
 * 4. Conflict Resolution (duplicates, overlaps)
 * 5. Review & Go-Live Settings
 * 6. Import Progress (real-time updates)
 * 7. Success & Next Steps
 */
function MigrationWizard({ type = "business" }) {
  const navigate = useNavigate();
  const { businessId, staffId } = useParams();
  const id = businessId || staffId;

  const [currentStep, setCurrentStep] = useState(1);
  const [migrationData, setMigrationData] = useState({
    type: type, // 'business' or 'freelancer'
    businessId: businessId,
    staffId: staffId,
    sourcePlatform: null, // 'square', 'squire', 'booksy', 'other'
    uploadedFiles: {
      services: null,
      customers: null,
      appointments: null,
      staff: null,
    },
    fieldMappings: {
      services: {},
      customers: {},
      appointments: {},
      staff: {},
    },
    conflicts: [],
    resolvedConflicts: {},
    goLiveSettings: {
      goLiveOption: "immediate",
      shadowMode: true,
      trialDays: null, // Will be fetched from platform settings
    },
    importResults: null,
  });

  const totalSteps = 7;

  // Save progress to localStorage
  useEffect(() => {
    localStorage.setItem(
      `migration_${type}_${id}`,
      JSON.stringify({
        currentStep,
        migrationData,
        lastSaved: new Date().toISOString(),
      }),
    );
  }, [currentStep, migrationData, type, id]);

  // Load saved progress on mount
  useEffect(() => {
    const saved = localStorage.getItem(`migration_${type}_${id}`);
    if (saved) {
      try {
        const { currentStep: savedStep, migrationData: savedData } =
          JSON.parse(saved);
        // Only prompt if there's actual progress (step > 1 or platform selected)
        if (savedStep > 1 || savedData.sourcePlatform) {
          const confirmed = window.confirm(
            "We found a saved migration in progress. Would you like to continue where you left off?",
          );
          if (confirmed) {
            setCurrentStep(savedStep);
            setMigrationData(savedData);
          } else {
            // Clear saved data if user declines
            localStorage.removeItem(`migration_${type}_${id}`);
          }
        }
      } catch (err) {
      }
    }
  }, [type, id]);

  const updateMigrationData = (updates) => {
    setMigrationData((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const goToStep = (step) => {
    if (step >= 1 && step <= totalSteps) {
      setCurrentStep(step);
      window.scrollTo(0, 0);
    }
  };

  const exitWizard = () => {
    const confirmed = window.confirm(
      "Are you sure you want to exit? Your progress will be saved and you can resume later.",
    );
    if (confirmed) {
      if (type === "freelancer") {
        navigate(`/dashboard/freelancer`);
      } else {
        navigate(`/dashboard`);
      }
    }
  };

  const renderStep = () => {
    const commonProps = {
      migrationData,
      updateMigrationData,
      nextStep,
      prevStep,
      goToStep,
      type, // Pass the entity type (business or freelancer)
    };

    switch (currentStep) {
      case 1:
        return <Step1Welcome {...commonProps} />;
      case 2:
        return <Step2Upload {...commonProps} />;
      case 3:
        return <Step3FieldMapping {...commonProps} />;
      case 4:
        return <Step4ConflictResolution {...commonProps} />;
      case 5:
        return <Step5ReviewGoLive {...commonProps} />;
      case 6:
        return <Step6Progress {...commonProps} />;
      case 7:
        return (
          <Step7Success
            {...commonProps}
            businessId={businessId}
            staffId={staffId}
          />
        );
      default:
        return <Step1Welcome {...commonProps} />;
    }
  };

  const LayoutComponent =
    type === "freelancer" ? FreelancerSidebarLayout : SidebarLayout;

  return (
    <LayoutComponent>
      <div style={styles.container}>
        {/* Header with Progress */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <h1 style={styles.title}>Platform Migration Wizard</h1>
            <button onClick={exitWizard} style={styles.exitButton}>
              Save & Exit
            </button>
          </div>

          {/* Progress Bar */}
          <div style={styles.progressContainer}>
            <div style={styles.progressBar}>
              <div
                style={{
                  ...styles.progressFill,
                  width: `${(currentStep / totalSteps) * 100}%`,
                }}
              />
            </div>
            <div style={styles.progressText}>
              Step {currentStep} of {totalSteps}
            </div>
          </div>

          {/* Step Indicators */}
          <div style={styles.stepIndicators}>
            {[
              { num: 1, label: "Welcome" },
              { num: 2, label: "Upload" },
              { num: 3, label: "Mapping" },
              { num: 4, label: "Conflicts" },
              { num: 5, label: "Review" },
              { num: 6, label: "Import" },
              { num: 7, label: "Complete" },
            ].map(({ num, label }) => (
              <div
                key={num}
                style={{
                  ...styles.stepIndicator,
                  ...(currentStep === num ? styles.stepIndicatorActive : {}),
                  ...(currentStep > num ? styles.stepIndicatorComplete : {}),
                }}
                onClick={() => currentStep > num && goToStep(num)}
              >
                <div style={styles.stepNumber}>
                  {currentStep > num ? "✓" : num}
                </div>
                <div style={styles.stepLabel}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div style={styles.content}>{renderStep()}</div>
      </div>
    </LayoutComponent>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
  },
  header: {
    backgroundColor: "#fff",
    borderBottom: "1px solid #e0e0e0",
    padding: "24px",
    position: "sticky",
    top: 0,
    zIndex: 100,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  headerContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    maxWidth: "1200px",
    margin: "0 auto",
    marginBottom: "20px",
  },
  title: {
    margin: 0,
    fontSize: "24px",
    fontWeight: 600,
    color: "#333",
  },
  exitButton: {
    padding: "8px 16px",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    color: "#666",
  },
  progressContainer: {
    maxWidth: "1200px",
    margin: "0 auto 20px",
  },
  progressBar: {
    width: "100%",
    height: "8px",
    backgroundColor: "#e0e0e0",
    borderRadius: "4px",
    overflow: "hidden",
    marginBottom: "8px",
  },
  progressFill: {
    height: "100%",
    backgroundColor: "#4CAF50",
    transition: "width 0.3s ease",
  },
  progressText: {
    textAlign: "center",
    fontSize: "14px",
    color: "#666",
  },
  stepIndicators: {
    display: "flex",
    justifyContent: "space-between",
    maxWidth: "1200px",
    margin: "0 auto",
    gap: "8px",
  },
  stepIndicator: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    cursor: "default",
  },
  stepIndicatorActive: {
    cursor: "default",
  },
  stepIndicatorComplete: {
    cursor: "pointer",
  },
  stepNumber: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    backgroundColor: "#e0e0e0",
    color: "#666",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    marginBottom: "8px",
    transition: "all 0.3s ease",
  },
  stepLabel: {
    fontSize: "12px",
    color: "#666",
    textAlign: "center",
  },
  content: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "32px 24px",
  },
};

// Update active step styling
styles.stepIndicatorActive.stepNumber = {
  backgroundColor: "#2196F3",
  color: "#fff",
};

// Update complete step styling
styles.stepIndicatorComplete.stepNumber = {
  backgroundColor: "#4CAF50",
  color: "#fff",
};

export default MigrationWizard;
